/**
 * 用户管理API服务
 */

import type { User } from './authApi';

// 导出User类型供其他模块使用
export type { User };

const API_BASE_URL = '/api';

/**
 * 用户列表响应
 */
export interface UserListResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      page: number;
      page_size: number;
      total: number;
      total_pages: number;
    };
  };
  message: string;
}

/**
 * 用户统计响应
 */
export interface UserStatsResponse {
  success: boolean;
  data: {
    stats: {
      total: number;
      admin_count: number;
      user_count: number;
      active_count: number;
      inactive_count: number;
      banned_count: number;
      // VIP统计
      vip_free_count: number;
      vip_vip_count: number;
      vip_vvip_count: number;
    };
  };
  message: string;
}

/**
 * VIP等级配置
 */
export const VIP_LEVELS = {
  0: { code: 'free', name: '免费用户', color: 'gray' },
  1: { code: 'vip', name: 'VIP会员', color: 'blue' },
  2: { code: 'vvip', name: 'VVIP会员', color: 'purple' },
  99: { code: 'admin', name: '管理员', color: 'red' },
} as const;

export type VipLevelKey = keyof typeof VIP_LEVELS;

/**
 * 获取认证token
 */
function getAuthHeaders(): HeadersInit {
  const authStore = localStorage.getItem('auth-storage');
  if (authStore) {
    const { state } = JSON.parse(authStore);
    const token = state?.accessToken;
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
  }
  return {
    'Content-Type': 'application/json',
  };
}

/**
 * 获取用户列表
 */
export async function getUserList(params: {
  page?: number;
  pageSize?: number;
  keyword?: string;
  role?: string;
  status?: string;
  vipLevel?: string; // VIP等级筛选
}): Promise<UserListResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.keyword) queryParams.append('keyword', params.keyword);
  if (params.role) queryParams.append('role', params.role);
  if (params.status) queryParams.append('status', params.status);
  if (params.vipLevel) queryParams.append('vipLevel', params.vipLevel);

  const response = await fetch(`${API_BASE_URL}/admin/users?${queryParams}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '获取用户列表失败');
  }

  return response.json();
}

/**
 * 获取用户详情
 */
export async function getUserDetail(userId: string) {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '获取用户详情失败');
  }

  return response.json();
}

/**
 * 删除用户
 */
export async function deleteUser(userId: string) {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '删除用户失败');
  }

  return response.json();
}

/**
 * 修改用户角色
 */
export async function updateUserRole(userId: string, role: string) {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/role`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '修改用户角色失败');
  }

  return response.json();
}

/**
 * 修改用户状态
 */
export async function updateUserStatus(userId: string, status: string) {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/status`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '修改用户状态失败');
  }

  return response.json();
}

/**
 * 创建用户
 */
export async function createUser(data: {
  username: string;
  email: string;
  password: string;
  role?: string;
}) {
  const response = await fetch(`${API_BASE_URL}/admin/users`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '创建用户失败');
  }

  return response.json();
}

/**
 * 获取用户统计
 */
export async function getUserStats(): Promise<UserStatsResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/users/stats`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '获取用户统计失败');
  }

  return response.json();
}

/**
 * 修改用户VIP状态
 */
export async function updateUserVip(userId: string, data: {
  vip_level: number;
  vip_expire_at?: string | null;
  vip_source?: string | null;
}) {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/vip`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '修改用户VIP状态失败');
  }

  return response.json();
}

/**
 * 获取VIP等级显示信息
 */
export function getVipLevelInfo(level: number | undefined) {
  const levelNum = level ?? 0;
  return VIP_LEVELS[levelNum as VipLevelKey] || VIP_LEVELS[0];
}

/**
 * 检查VIP是否过期
 */
export function isVipExpired(expireAt: string | null | undefined): boolean {
  if (!expireAt) return false; // 永久VIP
  return new Date(expireAt) < new Date();
}

/**
 * 格式化VIP过期时间显示
 */
export function formatVipExpireAt(expireAt: string | null | undefined): string {
  if (!expireAt) return '永久';
  const date = new Date(expireAt);
  if (date < new Date()) {
    return `已过期 (${date.toLocaleDateString('zh-CN')})`;
  }
  return date.toLocaleDateString('zh-CN');
}

/**
 * 格式化配额显示
 */
export function formatQuota(quota?: number): string {
  if (quota === undefined || quota === null) return '0';
  return quota.toLocaleString();
}

/**
 * 计算配额使用百分比
 */
export function calculateQuotaPercent(quota?: number, used?: number): number {
  if (!quota || quota === 0) return 0;
  const usedQuota = used || 0;
  return Math.round((usedQuota / quota) * 100);
}
